import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaPrayingHands, FaUserFriends, FaBell } from "react-icons/fa";
import { MdTempleBuddhist } from "react-icons/md";
import { buddhismAPI, orderAPI } from "../utils/api";

const Home = () => {
  const [featuredTemples, setFeaturedTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentPrayers, setRecentPrayers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedTemples = async () => {
      try {
        const response = await buddhismAPI.getAll({ size: 3 });
        setFeaturedTemples(response.data.results || []);
      } catch (error) {
        console.error("추천 사찰 정보 로딩 오류:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedTemples();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    const fetchRecentPrayers = async () => {
      try {
        const response = await orderAPI.getAll();
        const orders = response.data.results || response.data || [];
        const prayers = orders.slice(0, 20).flatMap((order) => {
          const items = order.items || [];
          return items.map((item) => ({
            id: order.id,
            templeName: order.store?.name || item.item?.temple_name || "",
            prayName: item.item?.name || item.name || "소원기도",
            buyerName: order.buyer_name || order.user?.name || "",
          }));
        });
        setRecentPrayers(prayers);
      } catch (error) {
        setRecentPrayers([]);
      }
    };
    fetchRecentPrayers();
  }, []);

  return (
    <div className="home">

      {/* ── 히어로 섹션 ── */}
      <section className="bud-hero">
        {/* 연꽃잎 배경 장식 - 왼쪽 하단 */}
        <div className="bud-lotus bud-lotus--bl" aria-hidden="true">
          <svg viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* 연꽃 꽃잎 레이어 */}
            {[0,40,80,120,160,200,240,280,320].map((deg, i) => (
              <ellipse key={i} cx="150" cy="150" rx="38" ry="110"
                fill="none" stroke="#C4A265" strokeWidth="0.8" opacity={0.18 - i*0.01}
                transform={`rotate(${deg} 150 150)`}/>
            ))}
            {[0,60,120,180,240,300].map((deg, i) => (
              <ellipse key={`i${i}`} cx="150" cy="150" rx="20" ry="70"
                fill="#C8A96E" opacity={0.07}
                transform={`rotate(${deg} 150 150)`}/>
            ))}
            <circle cx="150" cy="150" r="18" fill="#C8A96E" opacity="0.12"/>
            <circle cx="150" cy="150" r="10" fill="#C8A96E" opacity="0.18"/>
          </svg>
        </div>
        {/* 연꽃잎 배경 장식 - 오른쪽 상단 */}
        <div className="bud-lotus bud-lotus--tr" aria-hidden="true">
          <svg viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0,40,80,120,160,200,240,280,320].map((deg, i) => (
              <ellipse key={i} cx="150" cy="150" rx="38" ry="110"
                fill="none" stroke="#C4A265" strokeWidth="0.8" opacity={0.18 - i*0.01}
                transform={`rotate(${deg} 150 150)`}/>
            ))}
            {[0,60,120,180,240,300].map((deg, i) => (
              <ellipse key={`i${i}`} cx="150" cy="150" rx="20" ry="70"
                fill="#C8A96E" opacity={0.07}
                transform={`rotate(${deg} 150 150)`}/>
            ))}
            <circle cx="150" cy="150" r="18" fill="#C8A96E" opacity="0.12"/>
            <circle cx="150" cy="150" r="10" fill="#C8A96E" opacity="0.18"/>
          </svg>
        </div>
        {/* 작은 연꽃잎 - 중앙 좌우 */}
        <div className="bud-lotus bud-lotus--sm bud-lotus--ml" aria-hidden="true">
          <svg viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0,72,144,216,288].map((deg, i) => (
              <ellipse key={i} cx="80" cy="80" rx="20" ry="60"
                fill="#C8A96E" opacity="0.09"
                transform={`rotate(${deg} 80 80)`}/>
            ))}
            <circle cx="80" cy="80" r="10" fill="#C8A96E" opacity="0.13"/>
          </svg>
        </div>
        <div className="bud-lotus bud-lotus--sm bud-lotus--mr" aria-hidden="true">
          <svg viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0,72,144,216,288].map((deg, i) => (
              <ellipse key={i} cx="80" cy="80" rx="20" ry="60"
                fill="#C8A96E" opacity="0.09"
                transform={`rotate(${deg} 80 80)`}/>
            ))}
            <circle cx="80" cy="80" r="10" fill="#C8A96E" opacity="0.13"/>
          </svg>
        </div>

        <Container className="bud-hero-inner">
          <div className="bud-hero-lotus-icon" aria-hidden="true">
            <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
              {[0,60,120,180,240,300].map((deg, i) => (
                <ellipse key={i} cx="40" cy="40" rx="10" ry="28"
                  fill="#C8A96E" opacity="0.55"
                  transform={`rotate(${deg} 40 40)`}/>
              ))}
              <circle cx="40" cy="40" r="8" fill="#C8A96E" opacity="0.8"/>
            </svg>
          </div>

          <h1 className="bud-hero-title">비나이다</h1>
          <p className="bud-hero-sub">
            진심 어린 기도가 머무는 곳<br />
            언제 어디서나 마음을 모아 공양을 올리세요
          </p>

          <div className="bud-hero-divider" aria-hidden="true">
            <span>✦</span>
          </div>

          <div className="bud-hero-actions">
            <Link to="/pray" className="bud-btn bud-btn--primary">
              <FaPrayingHands /> 공양 신청하기
            </Link>
            <Link to="/live" className="bud-btn bud-btn--outline">
              <span className="bud-live-dot" /> 실시간 소원기도
            </Link>
          </div>
        </Container>
      </section>


      {/* ── 추천 사찰 섹션 ── */}
      <section className="bud-section bud-section--temples">
        <Container>
          <div className="bud-section-header">
            <span className="bud-section-badge">推薦 사찰</span>
            <h2 className="bud-section-title">추천 공양 사찰</h2>
            <p className="bud-section-desc">
              비나이다가 엄선한 전국의 사찰을 둘러보고 마음의 평화를 찾으세요
            </p>
            <div className="bud-section-line" aria-hidden="true" />
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: "var(--bud-teal)", width: "2.5rem", height: "2.5rem" }} />
            </div>
          ) : (
            <div className="bud-temple-grid">
              {featuredTemples.map((temple, idx) => (
                <div
                  key={temple.id}
                  className={`bud-tc ${temple.is_preparing ? 'bud-tc--preparing' : ''}`}
                  onClick={() => navigate(`/buddhism/${temple.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/buddhism/${temple.id}`)}
                >
                  <div className="bud-tc-img-wrap">
                    {temple.images && temple.images.length > 0 ? (
                      <img src={temple.images[0].url || temple.images[0]} alt={temple.name} className="bud-tc-img" />
                    ) : (
                      <div className="bud-tc-img-placeholder"><MdTempleBuddhist /></div>
                    )}
                    <div className="bud-tc-overlay">
                      <span className="bud-tc-overlay-link">공양 신청 →</span>
                    </div>
                    {temple.region && <span className="bud-tc-region">{temple.region}</span>}
                    {temple.is_preparing && <span className="bud-tc-preparing">준비중</span>}
                    {idx === 0 && !temple.is_preparing && <span className="bud-tc-best">🌟 BEST</span>}
                  </div>
                  <div className="bud-tc-body">
                    {temple.type && <span className="bud-tc-type">{temple.type}</span>}
                    <h3 className="bud-tc-name">{temple.name}</h3>
                    {temple.address && (
                      <p className="bud-tc-addr"><FaMapMarkerAlt /> {temple.address}</p>
                    )}
                    <div className="bud-tc-footer">
                      <span className="bud-tc-cta">공양 신청하기</span>
                      <span className="bud-tc-arrow">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center" style={{ marginTop: '40px' }}>
            <Link to="/pray" className="bud-btn bud-btn--outline-trad">
              전체 사찰 보기
            </Link>
          </div>
        </Container>
      </section>

      {/* ── 소개 섹션 ── */}
      <section className="bud-about-section">
        {/* 상단: 인용구 */}
        <div className="bud-quote-strip">
          <span className="bud-quote-dharma" aria-hidden="true">☸</span>
          <blockquote className="bud-quote">
            &ldquo;마음이 곧 부처이니, 기도하는 이 순간이 바로 깨달음의 문입니다.&rdquo;
          </blockquote>
          <span className="bud-quote-dharma" aria-hidden="true">☸</span>
        </div>

        <Container>
          <Row className="align-items-center g-5 py-5">
            {/* 왼쪽: 이미지 */}
            <Col lg={6}>
              <div className="bud-about-img-wrap">
                <img
                  src="/images/temple-feature.jpg"
                  alt="비나이다 소개"
                  className="bud-about-img"
                />
                {/* 이미지 위 골드 장식선 */}
                <div className="bud-about-img-deco" aria-hidden="true" />
              </div>
            </Col>

            {/* 오른쪽: 텍스트 */}
            <Col lg={6}>
              <span className="bud-section-badge">비나이다 소개</span>
              <h2 className="bud-about-title">
                비나이다와 함께하는<br />마음의 여정
              </h2>
              <div className="bud-about-line" aria-hidden="true" />
              <p className="bud-about-text">
                <strong>비나이다</strong>는 '기도하다'라는 순 우리말로,
                함께 기도하고 마음을 나누는 온라인 불교 플랫폼입니다.
              </p>
              <p className="bud-about-text">
                언제 어디서나 편리하게 공양을 신청하고, 전국 사찰의 정보를
                확인할 수 있습니다. 마음의 평화를 찾는 모든 분께 비나이다가 함께합니다.
              </p>

              {/* 수치 통계 제거 */}

              <Link to="/about" className="bud-btn bud-btn--primary bud-btn--sm" style={{ marginTop: "8px" }}>
                더 알아보기
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ─── 스타일 ─── */}
      <style jsx="true">{`
        /* ── 공통 변수 ── */
        :root {
          --bud-teal: #009590;
          --bud-teal-dark: #007873;
          --bud-teal-pale: #e8f5f5;
          --bud-teal-bg: #f0fafa;
          --bud-gold: #c8a96e;
          --bud-text: #2c3e35;
          --bud-muted: #6e8a84;
        }

        /* ── 히어로 ── */
        .bud-hero {
          position: relative;
          background: #FAF6ED; /* 전통 한지/베이지 테마 */
          padding: 110px 0 100px;
          overflow: hidden;
          text-align: center;
        }

        .bud-hero::before {
          content: "";
          position: absolute;
          top: 10px; left: 10px; right: 10px; bottom: 10px;
          border: 1px solid #E8DCC2;
          pointer-events: none;
        }

        .bud-hero-ornament { display: none; }

        /* 연꽃잎 배경 장식 */
        .bud-lotus {
          position: absolute;
          pointer-events: none;
        }
        .bud-lotus--bl {
          bottom: -80px; left: -80px;
          width: 420px; height: 420px;
          transform: rotate(-20deg);
        }
        .bud-lotus--tr {
          top: -80px; right: -80px;
          width: 380px; height: 380px;
          transform: rotate(30deg);
        }
        .bud-lotus--sm { width: 200px; height: 200px; }
        .bud-lotus--ml { top: 50%; left: 2%; transform: translateY(-50%) rotate(15deg); }
        .bud-lotus--mr { top: 50%; right: 2%; transform: translateY(-50%) rotate(-15deg); }

        .bud-hero-inner {
          position: relative;
          z-index: 2;
        }

        .bud-hero-lotus-icon {
          margin-bottom: 24px;
          display: inline-block;
          animation: budFloat 5s ease-in-out infinite;
        }
        @keyframes budFloat {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-8px); }
        }

        .bud-hero-title {
          font-family: 'Nanum Myeongjo', 'Batang', serif;
          font-size: 4.2rem;
          font-weight: 800;
          color: #3E3025;
          letter-spacing: -0.02em;
          margin-bottom: 24px;
          line-height: 1.1;
        }

        .bud-hero-sub {
          font-size: 1.1rem;
          color: #7A6A52;
          line-height: 2;
          margin-bottom: 36px;
          font-weight: 400;
          letter-spacing: 0.04em;
        }

        .bud-hero-divider {
          color: var(--bud-gold);
          font-size: 1rem;
          margin-bottom: 40px;
          letter-spacing: 1em;
          opacity: 0.7;
        }

        .bud-hero-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* ── 버튼 ── */
        .bud-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          border-radius: 3px;
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-decoration: none;
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
        }

        .bud-btn--primary {
          background-color: var(--bud-teal);
          color: #fff;
          box-shadow: 0 4px 18px rgba(0,149,144,0.22);
        }
        .bud-btn--primary:hover {
          background-color: var(--bud-teal-dark);
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,149,144,0.3);
        }

        .bud-btn--outline {
          background-color: transparent;
          color: var(--bud-teal);
          border: 1.5px solid var(--bud-teal);
        }
        .bud-btn--outline:hover {
          background-color: var(--bud-teal);
          color: #fff;
          transform: translateY(-2px);
        }

        .bud-btn--sm {
          padding: 10px 24px;
          font-size: 0.88rem;
        }

        .bud-live-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #e74c3c;
          display: inline-block;
          animation: budPulse 1.4s infinite;
          flex-shrink: 0;
        }

        @keyframes budPulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.55; transform: scale(0.8); }
        }

        /* ── 티커 배너 ── */
        .prayer-ticker-section {
          background: linear-gradient(90deg, #004d4b 0%, #009590 50%, #004d4b 100%);
          overflow: hidden;
        }
        .prayer-ticker-wrapper {
          display: flex;
          align-items: center;
          height: 46px;
        }
        .prayer-ticker-label {
          flex-shrink: 0;
          background: rgba(0,0,0,0.28);
          color: #fff;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          padding: 0 18px;
          height: 100%;
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          border-right: 1px solid rgba(255,255,255,0.18);
          text-transform: uppercase;
        }
        .prayer-ticker-track-outer {
          overflow: hidden;
          flex: 1;
          height: 100%;
          display: flex;
          align-items: center;
        }
        .prayer-ticker-track {
          display: flex;
          align-items: center;
          white-space: nowrap;
          animation: prayerScroll 40s linear infinite;
        }
        .prayer-ticker-track:hover { animation-play-state: paused; }
        .prayer-ticker-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0 20px;
          font-size: 0.86rem;
          color: rgba(255,255,255,0.92);
        }
        .prayer-ticker-pray  { font-weight: 600; color: #fff; }
        .prayer-ticker-temple { color: rgba(255,255,255,0.72); font-size: 0.8rem; }
        .prayer-ticker-buyer {
          background: rgba(255,255,255,0.15);
          color: #fff;
          font-size: 0.76rem;
          padding: 2px 8px;
          border-radius: 100px;
        }
        .prayer-ticker-divider {
          color: rgba(255,255,255,0.3);
          font-size: 0.65rem;
          margin-left: 8px;
        }
        @keyframes prayerScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        /* ── 섹션 공통 ── */
        .bud-section { padding: 80px 0; }
        .bud-section--light { background: #fff; }
        .bud-section--teal  { background: var(--bud-teal); }

        .bud-section-header { text-align: center; margin-bottom: 52px; }
        .bud-section-header--light {}

        .bud-section-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          color: var(--bud-teal);
          text-transform: uppercase;
          margin-bottom: 12px;
          padding: 5px 14px;
          border: 1px solid var(--bud-teal);
          border-radius: 2px;
        }
        .bud-section-badge--light {
          color: rgba(255,255,255,0.8);
          border-color: rgba(255,255,255,0.4);
        }

        .bud-section-title {
          font-family: 'Nanum Myeongjo', 'Batang', serif;
          font-size: 2.2rem;
          font-weight: 700;
          color: #3E3025;
          margin-bottom: 12px;
          letter-spacing: -0.01em;
        }
        .bud-section-title--light { color: #fff; font-family: 'Noto Sans KR', sans-serif; }

        .bud-section-desc {
          font-size: 1rem;
          color: var(--bud-muted);
          margin-bottom: 24px;
          line-height: 1.8;
        }
        .bud-section-desc--light { color: rgba(255,255,255,0.75); }

        .bud-section-line {
          width: 48px;
          height: 2px;
          background: var(--bud-teal);
          margin: 0 auto;
          opacity: 0.5;
        }
        .bud-section-line--light { background: rgba(255,255,255,0.5); }

        /* ── 사찰 카드 ── */
        .bud-temple-card {
          background: #fff;
          border: 1px solid #e8efee;
          border-radius: 4px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .bud-temple-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 36px rgba(0,149,144,0.12);
        }

        .bud-temple-img-wrap {
          position: relative;
          overflow: hidden;
          height: 200px;
        }
        .bud-temple-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .bud-temple-card:hover .bud-temple-img { transform: scale(1.06); }

        .bud-temple-card--preparing {
          opacity: 0.55;
          filter: grayscale(85%);
          background: #fafafa;
        }
        .bud-temple-card--preparing:hover {
          opacity: 0.8;
          filter: grayscale(40%);
        }

        .bud-temple-preparing {
          position: absolute;
          top: 14px; left: 14px;
          background: #d35400;
          color: #fff;
          font-size: 0.78rem;
          font-weight: 700;
          padding: 5px 12px;
          border-radius: 2px;
          letter-spacing: 0.08em;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 8px rgba(211,84,0,0.3);
          z-index: 10;
        }

        .bud-temple-img-placeholder {
          width: 100%;
          height: 100%;
          background: var(--bud-teal-pale);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: var(--bud-teal);
          opacity: 0.5;
        }

        .bud-temple-region {
          position: absolute;
          top: 14px;
          right: 14px;
          background: rgba(255,255,255,0.92);
          color: var(--bud-teal);
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 2px;
          letter-spacing: 0.06em;
          backdrop-filter: blur(4px);
        }

        .bud-temple-body {
          padding: 22px 24px 24px;
        }

        .bud-temple-type {
          display: inline-block;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--bud-teal);
          border: 1px solid var(--bud-teal);
          padding: 2px 9px;
          border-radius: 2px;
          margin-bottom: 10px;
          opacity: 0.8;
        }

        .bud-temple-name {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--bud-text);
          margin-bottom: 8px;
          letter-spacing: -0.01em;
        }

        .bud-temple-addr {
          font-size: 0.82rem;
          color: var(--bud-muted);
          display: flex;
          align-items: flex-start;
          gap: 6px;
          margin-bottom: 18px;
          line-height: 1.5;
        }
        .bud-temple-addr svg { flex-shrink: 0; margin-top: 2px; color: var(--bud-teal); }

        .bud-temple-link {
          font-size: 0.84rem;
          font-weight: 600;
          color: var(--bud-teal);
          letter-spacing: 0.04em;
          transition: letter-spacing 0.25s ease;
        }
        .bud-temple-card:hover .bud-temple-link { letter-spacing: 0.1em; }

        /* ── 서비스 카드 ── */
        .bud-service-card {
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 4px;
          padding: 40px 32px;
          text-align: center;
          transition: background 0.3s ease, transform 0.3s ease;
        }
        .bud-service-card:hover {
          background: rgba(255,255,255,0.18);
          transform: translateY(-5px);
        }

        .bud-service-icon {
          font-size: 2.2rem;
          color: rgba(255,255,255,0.9);
          margin-bottom: 20px;
          line-height: 1;
        }

        .bud-service-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 12px;
          letter-spacing: -0.01em;
        }

        .bud-service-desc {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.72);
          line-height: 1.8;
          margin: 0;
        }

        /* ── 소개 섹션 ── */
        .bud-about-section {
          background: #fff;
        }

        /* 인용구 띠 */
        .bud-quote-strip {
          background: var(--bud-teal-pale, #e8f5f5);
          border-top: 1px solid rgba(0,149,144,0.15);
          border-bottom: 1px solid rgba(0,149,144,0.15);
          padding: 22px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
        }

        .bud-quote-dharma {
          font-size: 1.3rem;
          color: var(--bud-teal);
          opacity: 0.45;
          flex-shrink: 0;
        }

        .bud-quote {
          font-size: 0.97rem;
          color: var(--bud-muted);
          font-style: italic;
          letter-spacing: 0.02em;
          line-height: 1.7;
          margin: 0;
          text-align: center;
        }

        /* 이미지 */
        .bud-about-img-wrap {
          position: relative;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0,149,144,0.12);
        }

        .bud-about-img {
          width: 100%;
          height: 420px;
          object-fit: cover;
          display: block;
          transition: transform 0.6s ease;
        }
        .bud-about-img-wrap:hover .bud-about-img { transform: scale(1.03); }

        .bud-about-img-deco {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, transparent, var(--bud-teal), transparent);
          opacity: 0.6;
        }

        .bud-about-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--bud-text);
          line-height: 1.4;
          margin: 14px 0 20px;
          letter-spacing: -0.02em;
        }

        .bud-about-line {
          width: 40px;
          height: 2px;
          background: var(--bud-teal);
          margin-bottom: 28px;
          opacity: 0.5;
        }

        .bud-about-text {
          font-size: 0.97rem;
          color: #4a6660;
          line-height: 1.9;
          margin-bottom: 18px;
        }

        /* 수치 통계 */
        .bud-stats {
          display: flex;
          gap: 0;
          border: 1px solid rgba(0,149,144,0.2);
          border-radius: 4px;
          overflow: hidden;
          margin: 28px 0 24px;
        }

        .bud-stat-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 18px 12px;
          border-right: 1px solid rgba(0,149,144,0.15);
          background: #f9fdfd;
          transition: background 0.25s;
        }
        .bud-stat-item:last-child { border-right: none; }
        .bud-stat-item:hover { background: var(--bud-teal-pale, #e8f5f5); }

        .bud-stat-num {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--bud-teal);
          letter-spacing: -0.02em;
          line-height: 1.2;
        }

        .bud-stat-label {
          font-size: 0.75rem;
          color: var(--bud-muted);
          margin-top: 4px;
          letter-spacing: 0.04em;
        }

        /* ── 추천 사찰 섹션 ── */
        .bud-section--temples {
          background: #FAF6ED;
          padding: 80px 0;
          position: relative;
        }
        .bud-section--temples::before {
          content: "";
          position: absolute;
          top: 20px; left: 20px; right: 20px; bottom: 20px;
          border: 1px solid #E8DCC2;
          pointer-events: none;
        }

        .bud-temple-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 0;
        }
        @media (max-width: 991px) { .bud-temple-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 575px) { .bud-temple-grid { grid-template-columns: 1fr; } }

        /* 새 카드 스타일 */
        .bud-tc {
          background: #fff;
          border: 1px solid #EAE0CC;
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
          display: flex;
          flex-direction: column;
        }
        .bud-tc:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(62,48,37,0.12);
        }
        .bud-tc--preparing {
          opacity: 0.5;
          filter: grayscale(85%);
        }
        .bud-tc--preparing:hover { opacity: 0.75; filter: grayscale(40%); }

        .bud-tc-img-wrap {
          position: relative;
          overflow: hidden;
          height: 220px;
        }
        .bud-tc-img {
          width: 100%; height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .bud-tc:hover .bud-tc-img { transform: scale(1.08); }

        .bud-tc-img-placeholder {
          width: 100%; height: 100%;
          background: #F0EAD6;
          display: flex; align-items: center; justify-content: center;
          font-size: 3.5rem;
          color: #C4A265;
          opacity: 0.6;
        }

        /* 호버 오버레이 */
        .bud-tc-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(30,20,10,0.75) 0%, rgba(30,20,10,0) 60%);
          display: flex;
          align-items: flex-end;
          padding: 20px;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .bud-tc:hover .bud-tc-overlay { opacity: 1; }
        .bud-tc-overlay-link {
          color: #fff;
          font-size: 0.9rem;
          font-weight: 700;
          letter-spacing: 0.06em;
        }

        /* 배지 */
        .bud-tc-region {
          position: absolute; top: 12px; right: 12px;
          background: rgba(255,255,255,0.9);
          color: #009590;
          font-size: 0.72rem; font-weight: 700;
          padding: 3px 10px; border-radius: 2px;
          letter-spacing: 0.06em;
          backdrop-filter: blur(4px);
        }
        .bud-tc-preparing {
          position: absolute; top: 12px; left: 12px;
          background: #d35400;
          color: #fff;
          font-size: 0.72rem; font-weight: 700;
          padding: 4px 10px; border-radius: 2px;
          letter-spacing: 0.08em;
          box-shadow: 0 2px 8px rgba(211,84,0,0.3);
          z-index: 10;
        }
        .bud-tc-best {
          position: absolute; top: 12px; left: 12px;
          background: linear-gradient(135deg, #C8A96E, #A07C40);
          color: #fff;
          font-size: 0.72rem; font-weight: 700;
          padding: 4px 10px; border-radius: 2px;
          letter-spacing: 0.06em;
          box-shadow: 0 2px 8px rgba(160,124,64,0.3);
        }

        /* 카드 바디 */
        .bud-tc-body {
          padding: 20px 22px 22px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .bud-tc-type {
          display: inline-block;
          font-size: 0.68rem; font-weight: 700;
          letter-spacing: 0.1em;
          color: #009590;
          border: 1px solid #009590;
          padding: 2px 8px; border-radius: 2px;
          margin-bottom: 8px; opacity: 0.8;
        }
        .bud-tc-name {
          font-family: 'Nanum Myeongjo', 'Batang', serif;
          font-size: 1.25rem; font-weight: 700;
          color: #3E3025;
          margin-bottom: 8px; letter-spacing: -0.01em;
        }
        .bud-tc-addr {
          font-size: 0.8rem; color: #6e8a84;
          display: flex; align-items: flex-start;
          gap: 5px; margin-bottom: 0; line-height: 1.5;
          flex: 1;
        }
        .bud-tc-addr svg { flex-shrink: 0; margin-top: 2px; color: #009590; }
        .bud-tc-footer {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 16px;
          padding-top: 14px;
          border-top: 1px solid #EAE0CC;
        }
        .bud-tc-cta {
          font-size: 0.82rem; font-weight: 700;
          color: #3E3025; letter-spacing: 0.04em;
        }
        .bud-tc-arrow {
          font-size: 1rem; color: #C4A265;
          transition: transform 0.25s;
        }
        .bud-tc:hover .bud-tc-arrow { transform: translateX(4px); }

        /* 전체 보기 버튼 */
        .bud-btn--outline-trad {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 36px;
          border: 1.5px solid #C4A265;
          color: #3E3025;
          background: transparent;
          border-radius: 3px;
          font-size: 0.92rem; font-weight: 700;
          letter-spacing: 0.06em;
          text-decoration: none;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .bud-btn--outline-trad:hover {
          background: #C4A265;
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(196,162,101,0.3);
        }

        /* ── 서비스 섹션 (전통 테마) ── */
        .bud-section--trad {
          background: #3E3025;
          padding: 80px 0;
        }
        .bud-section-badge--gold {
          color: #C4A265;
          border-color: #C4A265;
        }
        .bud-section-title--dark { color: #FAF0DC; font-family: 'Nanum Myeongjo', 'Batang', serif; }
        .bud-section-line--gold { background: #C4A265; }

        .bud-svc-card {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(196,162,101,0.3);
          border-radius: 6px;
          padding: 40px 28px;
          text-align: center;
          cursor: pointer;
          transition: background 0.3s ease, transform 0.3s ease, border-color 0.3s;
          height: 100%;
        }
        .bud-svc-card:hover {
          background: rgba(196,162,101,0.12);
          border-color: rgba(196,162,101,0.6);
          transform: translateY(-6px);
        }
        .bud-svc-emoji {
          font-size: 2.8rem;
          margin-bottom: 20px;
          display: block;
        }
        .bud-svc-title {
          font-family: 'Nanum Myeongjo', 'Batang', serif;
          font-size: 1.25rem; font-weight: 700;
          color: #FAF0DC;
          margin-bottom: 12px;
        }
        .bud-svc-desc {
          font-size: 0.88rem;
          color: rgba(250,240,220,0.65);
          line-height: 1.85;
          margin-bottom: 20px;
        }
        .bud-svc-link {
          display: inline-block;
          font-size: 0.82rem; font-weight: 700;
          color: #C4A265;
          letter-spacing: 0.06em;
          transition: letter-spacing 0.25s;
        }
        .bud-svc-card:hover .bud-svc-link { letter-spacing: 0.12em; }

        /* ── 반응형 ── */
        @media (max-width: 768px) {
          .bud-hero { padding: 70px 0 60px; }
          .bud-hero-title { font-size: 2.8rem; }
          .bud-hero-ornament { width: 220px; height: 220px; }
          .bud-section { padding: 60px 0; }
          .bud-section--temples::before { display: none; }
        }

        @media (max-width: 480px) {
          .bud-hero-title { font-size: 2.2rem; }
          .bud-hero-ornament { display: none; }
          .bud-hero-actions { flex-direction: column; align-items: center; }
          .bud-btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default Home;